import React, { useState } from 'react';
import logo from 'media/Logo/SLogo.svg';
import SVG from 'components/renderSvg';

import InputField from 'components/inputField';
import Button from 'components/button';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import emailIcon from '../../../media/svgs/email.svg';
import passwordIcon from '../../../media/svgs/password.svg';
import useAuth from 'hooks/useAuth';
import useDocument from 'hooks/useDocument';
import { storeVenderData } from 'store/slices/venderslice';
import { useDispatch } from 'react-redux';
import { loginValidationSchema } from 'schema';
import useVendor from 'hooks/useVendor';
import SplitAuthLayout from '../components/SplitAuthLayout';

const LoginPage = () => {
  const [loader, setLoader] = useState(false);
  const navigate = useNavigate();
  const { loginUser } = useAuth();
  const dispatch = useDispatch();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(loginValidationSchema),
    defaultValues: {
      email: '',
      password: '',
    },
  });

  const { fetchUserById } = useDocument(`merchants`);
  const { fetchVenderById } = useVendor();

  const onSubmit = async data => {
    setLoader(true);
    try {
      const response = await loginUser(data);
      if (response?.status) {
        const res = await fetchUserById(response?.id);
        if (res?.success && res?.data) {
          // Handle API response structure - could be direct object or { data: [...] }
          let vendorData = res.data;

          // Check if vendorData is an object with a data property (nested structure: { data: [...] })
          if (vendorData && typeof vendorData === 'object' && !Array.isArray(vendorData) && vendorData.data && !vendorData.id) {
            vendorData = Array.isArray(vendorData.data) ? vendorData.data[0] : vendorData.data;
          }
          // Check if vendorData is an array
          else if (Array.isArray(vendorData)) {
            vendorData = vendorData[0];
          }
          // Also handle if res.data.data exists (nested data from API wrapper)
          else if (res?.data?.data && (!vendorData || (typeof vendorData === 'object' && !vendorData.id))) {
            vendorData = Array.isArray(res.data.data) ? res.data.data[0] : res.data.data;
          }

          // Validate vendorData object - should have at least id or subscription_tier
          if (!vendorData || typeof vendorData !== 'object' || Array.isArray(vendorData)) {
            console.error('Invalid vendor data structure:', res.data);
            setLoader(false);
            return;
          }
          if (!vendorData.id && !vendorData.subscription_tier && !vendorData.email) {
            console.error('Vendor data missing required fields:', vendorData);
            setLoader(false);
            return;
          }

          // Check if profile is completed (you may need to adjust this based on API response)
          const profileCompleted = vendorData.profileCompleted || false;

          if (profileCompleted) {
            const venderData = await fetchVenderById();
            if (venderData?.result) {
              dispatch(storeVenderData({ ...vendorData, ...venderData }));
            } else {
              dispatch(storeVenderData(vendorData));
            }
          } else {
            dispatch(storeVenderData(vendorData));
          }

          // Gate by subscription/trial
          const now = Date.now();

          // Handle trial_ends_at - check if it's a valid date (not the default "0001-01-01" date)
          // Normalize trial date check with fallback for trial_end
          let trialEnds = 0;
          const trialEndsSource = vendorData.trial_ends_at || vendorData.trial_end || vendorData.trialEndsAtMillis;

          if (trialEndsSource) {
            if (typeof trialEndsSource === 'number') {
              trialEnds = trialEndsSource;
            } else {
              const trialDate = new Date(trialEndsSource).getTime();
              // Check if date is valid and not the default zero date (before 1970)
              if (trialDate > 0 && trialDate > new Date('1970-01-01').getTime()) {
                trialEnds = trialDate;
              }
            }
          }

          // Fallback to localStorage if trial date is missing in profile
          if (trialEnds === 0) {
            const localTrialEnd = localStorage.getItem('trial_end');
            if (localTrialEnd) {
              const trialDate = new Date(localTrialEnd).getTime();
              if (trialDate > now) {
                trialEnds = trialDate;
              }
            }
          }

          const status = vendorData.subscription_tier || vendorData.status || 'free';
          const isSubscribed = status === 'pro' || status === 'active';
          // Trial is active if we have a future end date, regardless of status (unless subscribed)
          const isTrialActive = !isSubscribed && trialEnds > 0 && now <= trialEnds;

          if (!(isTrialActive || isSubscribed)) {
            navigate('/plans');
          } else {
            const onboardingIncomplete = !vendorData.serviceCompleted || !vendorData.addBatch;
            if (onboardingIncomplete) {
              navigate('/setup/jumpstart');
            } else {
              navigate('/');
            }
          }
          setLoader(false);
          return;
        }
        // If profile fetch failed, still try to navigate
        dispatch(storeVenderData({ email: response.email }));
        navigate('/setup/jumpstart');
        setLoader(false);
      } else {
        // Handle specific error for unverified email
        if (response?.error?.toLowerCase()?.includes('verify your email')) {
          navigate('/verify-otp', { state: { email: data.email } });
        }
        setLoader(false);
      }
    } catch (error) {
      console.error('Login submission error:', error);
      setLoader(false);
    }
  };
  return (
    <SplitAuthLayout>
      <div className='flex flex-col items-center mb-8'>
        <img src={logo} alt='Sportona Icon' className='w-12 h-12 mb-6 object-contain' />
        <h2 className='text-[28px] font-bold text-[#111] mb-2 text-center'>
          Sign in to your business
        </h2>
        <p className='text-[#666] text-center text-sm px-4'>
          Sign in the same way you do in the app to access your dashboard.
        </p>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className='flex flex-col gap-4 w-full'>
        <InputField
          type='email'
          name='email'
          label='Email address'
          placeholder='Your email here'
          error={errors?.email?.message}
          register={register}
          data-cy='login-email'
          mb={false}
        />

        <InputField
          type='password'
          label='Password'
          name='password'
          placeholder='Enter your password'
          error={errors?.password?.message}
          register={register}
          data-cy='login-password'
          mb={false}
        />

        <div className='flex justify-between items-center -mt-2'>
          <span className='text-xs text-gray-500'></span>
          <span
            className='text-sm font-medium text-black cursor-pointer hover:underline'
            onClick={() => navigate('/forgot-password')}
          >
            Email me a code / Forgot password?
          </span>
        </div>

        <Button
          title='Log in'
          variant='primary'
          className='w-full mt-2 !bg-black !text-white'
          loading={loader}
          loadingTitle='Logging in...'
          data-cy='login-submit'
          style={{ backgroundColor: '#000000', color: '#ffffff' }}
        />

        <div className='mt-8 text-center text-sm text-gray-600'>
          New to Sportona?{' '}
          <span
            className='font-semibold text-black cursor-pointer hover:underline'
            onClick={() => navigate('/register')}
          >
            Create an account
          </span>
        </div>
      </form>
    </SplitAuthLayout>
  );
};

export default LoginPage;
